/**
 * WriterSaveModal — pick client + folder, choose format, save the generated letter.
 *
 * Opened from WriterDrawer when user clicks "Save to client folder".
 * Reuses existing endpoints: clients.search, clients.getFolders.
 * Calls new endpoint: writer.save.
 */
import React, { useState, useEffect, useRef } from 'react';
import { clients, writer as writerApi } from './api.js';

export default function WriterSaveModal({ darkMode = true, content, suggestedTitle, onClose, onSaved }) {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const [folderTree, setFolderTree] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});

  const [format, setFormat] = useState('docx');
  const [filename, setFilename] = useState(suggestedTitle || 'Letter');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const searchTimerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Theme
  const bg = darkMode ? '#1e1e1e' : '#ffffff';
  const bgSecondary = darkMode ? '#2a2a2a' : '#f7f7f7';
  const bgHover = darkMode ? '#333333' : '#eaeaea';
  const border = darkMode ? '#3a3a3a' : '#dddddd';
  const text1 = darkMode ? '#e0e0e0' : '#222222';
  const text2 = darkMode ? '#999999' : '#666666';
  const text3 = darkMode ? '#666666' : '#999999';
  const accent = '#185FA5';
  const accentBg = darkMode ? '#0F2A4A' : '#E6F1FB';
  const success_color = '#27AE60';
  const danger = '#A32D2D';

  // Auto-focus search input
  useEffect(() => {
    setTimeout(() => searchInputRef.current?.focus(), 50);
  }, []);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Debounced client search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query.trim() || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const r = await clients.search(query.trim());
        setSearchResults(r.data?.results || r.data || []);
      } catch (e) {
        setError('Search failed: ' + (e.message || ''));
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [query]);

  // Load folders when a client is picked
  const pickClient = async (c) => {
    setSelectedClient(c);
    setSelectedFolderId(null);
    setLoadingFolders(true);
    setError('');
    try {
      const r = await clients.getFolders(c.id);
      const tree = r.data?.folders || r.data || [];
      setFolderTree(tree);
      // Auto-expand top level
      const expanded = {};
      tree.forEach(f => { if (f.id) expanded[f.id] = true; });
      setExpandedFolders(expanded);
    } catch (e) {
      setError('Could not load folders: ' + (e.message || ''));
    } finally {
      setLoadingFolders(false);
    }
  };

  const toggleFolder = (id) => {
    setExpandedFolders(s => ({ ...s, [id]: !s[id] }));
  };

  // Recursive folder row renderer
  const renderFolders = (folders, depth = 0) => {
    if (!Array.isArray(folders)) return null;
    return folders.map(f => {
      const hasChildren = f.children && f.children.length > 0;
      const isExpanded = expandedFolders[f.id];
      const isSelected = selectedFolderId === f.id;
      return (
        <div key={f.id}>
          <div
            onClick={() => setSelectedFolderId(f.id)}
            style={{
              display: 'flex', alignItems: 'center',
              padding: '4px 8px', paddingLeft: `${8 + depth * 16}px`,
              cursor: 'pointer', fontSize: '13px',
              background: isSelected ? accentBg : 'transparent',
              color: isSelected ? accent : text1,
              borderRadius: '3px',
            }}
            onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = bgHover; }}
            onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
          >
            {hasChildren ? (
              <span
                onClick={(e) => { e.stopPropagation(); toggleFolder(f.id); }}
                style={{
                  display: 'inline-block', width: '16px', textAlign: 'center',
                  cursor: 'pointer', color: text2,
                }}
              >{isExpanded ? '▾' : '▸'}</span>
            ) : (
              <span style={{ display: 'inline-block', width: '16px' }}></span>
            )}
            <span style={{ marginLeft: '4px' }}>📁 {f.name}</span>
          </div>
          {hasChildren && isExpanded && renderFolders(f.children, depth + 1)}
        </div>
      );
    });
  };

  const handleSave = async () => {
    if (!selectedClient || !selectedFolderId || !filename.trim() || saving) return;
    setSaving(true);
    setError('');
    setSuccess(null);
    try {
      const r = await writerApi.save({
        client_id: selectedClient.id,
        folder_id: selectedFolderId,
        filename: filename.trim(),
        format,
        content,
        description: 'Generated by Writer',
      });
      setSuccess({
        filename: r.data.filename,
        indexed: r.data.indexed,
      });
      if (onSaved) onSaved(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const canSave = selectedClient && selectedFolderId && filename.trim() && !saving;

  // Render
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9100,
        }}
      />
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: '90%', maxWidth: '720px', maxHeight: '85vh',
          background: bg, border: `1px solid ${border}`, borderRadius: '6px',
          zIndex: 9101, display: 'flex', flexDirection: 'column',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          color: text1, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: '15px', fontWeight: 500 }}>Save letter to client folder</span>
          <button
            onClick={onClose}
            style={{
              fontSize: '14px', padding: '4px 10px', background: 'transparent',
              border: `1px solid ${border}`, borderRadius: '4px', color: text1, cursor: 'pointer',
            }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{
          padding: '14px 18px', overflowY: 'auto', flex: 1,
          display: 'flex', flexDirection: 'column', gap: '14px',
        }}>

          {/* If saved, show success and close */}
          {success ? (
            <div style={{
              padding: '14px', background: darkMode ? '#1a3320' : '#e8f5e9',
              border: `1px solid ${success_color}`, borderRadius: '4px',
            }}>
              <div style={{ color: success_color, fontWeight: 500, marginBottom: '6px' }}>
                ✓ Saved
              </div>
              <div style={{ fontSize: '13px', color: text1 }}>
                Saved as <strong>{success.filename}</strong> in {selectedClient?.name}.
              </div>
              {success.indexed && (
                <div style={{ fontSize: '12px', color: text2, marginTop: '6px' }}>
                  Queued for InsureIQ indexing — will be RAG-searchable in a few minutes.
                </div>
              )}
              <div style={{ marginTop: '12px' }}>
                <button
                  onClick={onClose}
                  style={{
                    fontSize: '13px', padding: '6px 14px', background: accent,
                    border: 'none', borderRadius: '4px', color: '#fff', cursor: 'pointer',
                  }}
                >Done</button>
              </div>
            </div>
          ) : (
            <>
              {/* Client picker */}
              <div>
                <div style={{
                  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px',
                  color: text3, marginBottom: '4px',
                }}>1. Client</div>

                {selectedClient ? (
                  <div style={{
                    padding: '8px 12px', background: accentBg, border: `1px solid ${accent}`,
                    borderRadius: '4px', display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <span style={{ color: accent, fontWeight: 500 }}>{selectedClient.name}</span>
                    <button
                      onClick={() => { setSelectedClient(null); setFolderTree([]); setSelectedFolderId(null); }}
                      style={{
                        fontSize: '12px', padding: '3px 8px', background: 'transparent',
                        border: `1px solid ${accent}`, borderRadius: '3px', color: accent, cursor: 'pointer',
                      }}
                    >Change</button>
                  </div>
                ) : (
                  <>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      placeholder="Type at least 2 characters to search..."
                      style={{
                        width: '100%', padding: '8px 10px', fontSize: '13px',
                        background: bgSecondary, border: `1px solid ${border}`,
                        borderRadius: '4px', color: text1, boxSizing: 'border-box',
                      }}
                    />
                    {searching && (
                      <div style={{ fontSize: '12px', color: text3, marginTop: '4px', fontStyle: 'italic' }}>
                        Searching...
                      </div>
                    )}
                    {searchResults.length > 0 && (
                      <div style={{
                        marginTop: '6px', maxHeight: '180px', overflowY: 'auto',
                        border: `1px solid ${border}`, borderRadius: '4px',
                      }}>
                        {searchResults.slice(0, 30).map(c => (
                          <div
                            key={c.id}
                            onClick={() => pickClient(c)}
                            style={{
                              padding: '6px 10px', fontSize: '13px', cursor: 'pointer',
                              borderBottom: `1px solid ${border}`,
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = bgHover}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            {c.name}
                            {c.docsvault_id && (
                              <span style={{ marginLeft: '8px', color: text3, fontSize: '11px' }}>
                                #{c.docsvault_id}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Folder picker */}
              {selectedClient && (
                <div>
                  <div style={{
                    fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px',
                    color: text3, marginBottom: '4px',
                  }}>2. Folder</div>
                  {loadingFolders ? (
                    <div style={{ fontSize: '13px', color: text3, fontStyle: 'italic', padding: '8px' }}>
                      Loading folders...
                    </div>
                  ) : folderTree.length === 0 ? (
                    <div style={{ fontSize: '13px', color: text3, fontStyle: 'italic', padding: '8px' }}>
                      No folders found for this client.
                    </div>
                  ) : (
                    <div style={{
                      maxHeight: '200px', overflowY: 'auto', padding: '6px',
                      background: bgSecondary, border: `1px solid ${border}`, borderRadius: '4px',
                    }}>
                      {renderFolders(folderTree)}
                    </div>
                  )}
                </div>
              )}

              {/* Format + filename */}
              {selectedClient && selectedFolderId && (
                <>
                  <div>
                    <div style={{
                      fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px',
                      color: text3, marginBottom: '4px',
                    }}>3. Format</div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {['docx', 'pdf', 'txt'].map(f => (
                        <label key={f} style={{
                          padding: '6px 12px', fontSize: '13px', cursor: 'pointer',
                          background: format === f ? accentBg : 'transparent',
                          border: `1px solid ${format === f ? accent : border}`,
                          borderRadius: '4px',
                          color: format === f ? accent : text1,
                        }}>
                          <input
                            type="radio" name="format" value={f}
                            checked={format === f} onChange={() => setFormat(f)}
                            style={{ display: 'none' }}
                          />
                          .{f}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div style={{
                      fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px',
                      color: text3, marginBottom: '4px',
                    }}>4. Filename</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '12px', color: text2 }}>
                        {new Date().toISOString().slice(0, 10)} -
                      </span>
                      <input
                        type="text"
                        value={filename}
                        onChange={e => setFilename(e.target.value)}
                        placeholder="Letter title"
                        style={{
                          flex: 1, padding: '6px 10px', fontSize: '13px',
                          background: bgSecondary, border: `1px solid ${border}`,
                          borderRadius: '4px', color: text1,
                        }}
                      />
                      <span style={{ fontSize: '12px', color: text2 }}>.{format}</span>
                    </div>
                  </div>
                </>
              )}

              {/* Error display */}
              {error && (
                <div style={{
                  padding: '8px 12px', background: darkMode ? '#3a1a1a' : '#fdecea',
                  border: `1px solid ${danger}`, borderRadius: '4px',
                  fontSize: '13px', color: danger,
                }}>{error}</div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div style={{
            padding: '12px 18px', borderTop: `1px solid ${border}`,
            display: 'flex', justifyContent: 'flex-end', gap: '8px',
          }}>
            <button
              onClick={onClose}
              style={{
                padding: '7px 14px', fontSize: '13px', background: 'transparent',
                border: `1px solid ${border}`, borderRadius: '4px', color: text1, cursor: 'pointer',
              }}
            >Cancel</button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              style={{
                padding: '7px 14px', fontSize: '13px', fontWeight: 500,
                background: canSave ? accent : bgSecondary,
                border: `1px solid ${canSave ? accent : border}`,
                borderRadius: '4px',
                color: canSave ? '#fff' : text3,
                cursor: canSave ? 'pointer' : 'not-allowed',
              }}
            >{saving ? 'Saving...' : 'Save'}</button>
          </div>
        )}
      </div>
    </>
  );
}
